import { ScrollingModule } from '@angular/cdk/scrolling';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { CheckboxComponent } from '../core/controls/checkbox.component';

/**
 * Locations → Accounts: unit rows using shared {@link CheckboxComponent} (PrimeNG),
 * keeping selection in sync with the parent form's `accountUnitIds` array.
 */
@Component({
  selector: 'app-location-account-units-panel',
  standalone: true,
  imports: [ReactiveFormsModule, CheckboxComponent, ScrollingModule],
  template: `
    <cdk-virtual-scroll-viewport
      [itemSize]="ROW_HEIGHT_PX"
      class="tw-block tw-pl-2"
      [style.height.px]="viewportHeightPx"
    >
      <div
        *cdkVirtualFor="let opt of unitOptions; trackBy: trackByValue"
        class="tw-text-sm"
      >
        <app-checkbox [control]="controlFor(opt.value)" [label]="opt.label" />
      </div>
    </cdk-virtual-scroll-viewport>
  `,
})
export class LocationAccountUnitsPanelComponent
  implements OnChanges, OnDestroy
{
  @Input({ required: true })
  unitOptions: { label: string; value: string }[] = [];

  @Input({ required: true })
  selectedKeys: string[] = [];

  @Output()
  readonly selectionChange = new EventEmitter<string[]>();

  private readonly controls = new Map<string, FormControl<boolean>>();
  private subs = new Subscription();

  protected readonly ROW_HEIGHT_PX = 32;
  private readonly MAX_VIEWPORT_PX = 280;

  /** Height of the virtual viewport — grows with item count up to a cap so a
   *  short list does not show 280px of empty whitespace. */
  get viewportHeightPx(): number {
    const len = this.unitOptions?.length ?? 0;
    return Math.min(len * this.ROW_HEIGHT_PX, this.MAX_VIEWPORT_PX);
  }

  protected readonly trackByValue = (_: number, opt: { value: string }) =>
    opt.value;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['unitOptions']) {
      this.resetControls();
    }
    if (changes['selectedKeys']) {
      this.syncControlsFromSelectedKeys();
    }
  }

  ngOnDestroy(): void {
    this.resetControls();
  }

  controlFor(key: string): FormControl<boolean> {
    let c = this.controls.get(key);
    if (!c) {
      c = new FormControl(this.selectedKeys.includes(key), {
        nonNullable: true,
      });
      this.subs.add(
        c.valueChanges.subscribe((v) => {
          if (this.selectedKeys.includes(key) !== v) {
            this.emitUpdatedKeys(key, v);
          }
        }),
      );
      this.controls.set(key, c);
    }
    return c;
  }

  private emitUpdatedKeys(key: string, checked: boolean): void {
    const cur = new Set(this.selectedKeys);
    if (checked) {
      cur.add(key);
    } else {
      cur.delete(key);
    }
    this.selectionChange.emit([...cur]);
  }

  private syncControlsFromSelectedKeys(): void {
    for (const [key, c] of this.controls) {
      const want = this.selectedKeys.includes(key);
      if (c.value !== want) {
        c.setValue(want, { emitEvent: false });
      }
    }
  }

  private resetControls(): void {
    this.subs.unsubscribe();
    this.subs = new Subscription();
    this.controls.clear();
  }
}
